import React, { useState, useEffect } from 'react';
import ClipGenerator from './components/ClipGenerator';
import BatchProcessor from './components/BatchProcessor';
import ApiAccess from './components/ApiAccess';
import PricingSection from './components/PricingSection';
import { HomepageMobileNav, BackToTop } from './components/MobileNav';
import BusinessFeatures from './components/BusinessFeatures';
import UserDashboard from './components/UserDashboard';
import AuthModal from './components/AuthModal';
import ReferralLanding from './components/ReferralLanding';
import NotificationsPanel from './components/NotificationsPanel';
import BillingPanel from './components/BillingPanel';
import DocumentationPanel from './components/DocumentationPanel';
import SupportPanel from './components/SupportPanel';
import SharedClipsViewer from './components/SharedClipsViewer';
// Removed AccountInbox component - using direct email replies instead
import AIInsightsPanel from './components/AIInsightsPanel';
import PlanDetailsPanel from './components/PlanDetailsPanel';
import UpgradePanel from './components/UpgradePanel';
import TermsOfService from './components/TermsOfService';
import PrivacyPolicy from './components/PrivacyPolicy';
import { UserProvider, useUser } from './contexts/UserContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { ProcessingProvider } from './contexts/ProcessingContext';
import CurrencySelector from './components/CurrencySelector';
import LanguageSelector from './components/LanguageSelector';
import HelpChat from './components/HelpChat';
import ProcessingIndicator from './components/ProcessingIndicator';
import { useProcessing } from './contexts/ProcessingContext';
// MultilingualSlider replaced with inline language badge
import VideoShowcase from './components/VideoShowcase';
import DemoClips from './components/DemoClips';
import './App.css';

const AppContent = () => {
  const { user, setUser } = useUser();
  const { processingState } = useProcessing();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [activeFeature, setActiveFeature] = useState(null);
  const [currentView, setCurrentView] = useState(() => {
    // Check URL for direct navigation (email links like ?view=generator#clips)
    const params = new URLSearchParams(window.location.search);
    const viewParam = params.get('view');
    if (viewParam) return viewParam;
    const path = window.location.pathname.substring(1);
    if (path && path !== '/') return path;
    return 'dashboard';
  });
  const [navigationHistory, setNavigationHistory] = useState(['dashboard']);

  // Enhanced navigation with browser-like back button functionality
  const handleNavigateToApp = (section) => {
    // Store scroll position and source section before navigating
    if (currentView === 'dashboard') {
      sessionStorage.setItem('dashboardScrollY', window.scrollY.toString());
    }
    setNavigationHistory(prev => [...prev, section]);
    setCurrentView(section);
    window.scrollTo(0, 0);
    
    // Update browser URL without page refresh for better UX
    if (window.history && window.history.pushState) {
      window.history.pushState({ section }, '', `/${section}`);
    }
  };

  const handleBackToDashboard = () => {
    setNavigationHistory(prev => [...prev, 'dashboard']);
    setCurrentView('dashboard');
    
    // Update browser URL
    if (window.history && window.history.pushState) {
      window.history.pushState({ section: 'dashboard' }, '', '/dashboard');
    }
    
    // Restore scroll position after render
    setTimeout(() => {
      const savedY = sessionStorage.getItem('dashboardScrollY');
      if (savedY) {
        window.scrollTo(0, parseInt(savedY));
        sessionStorage.removeItem('dashboardScrollY');
      }
    }, 100);
  };

  // Handle browser back button
  useEffect(() => {
    const handlePopState = (event) => {
      if (event.state && event.state.section) {
        setCurrentView(event.state.section);
      } else {
        // If no state, determine from URL
        const path = window.location.pathname.substring(1) || 'dashboard';
        setCurrentView(path);
      }
    };

    // Set initial URL state
    // Handle referral links - extract ref code before routing
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref');
    if (refCode) {
      sessionStorage.setItem('referralCode', refCode);
    }
    
    const initialPath = window.location.pathname.substring(1) || 'dashboard';
    if (initialPath !== 'dashboard') {
      setCurrentView(initialPath);
    }
    // Handle email link: ?view=generator#clips — navigate to generator and scroll to clips
    const viewParam = new URLSearchParams(window.location.search).get('view');
    if (viewParam) {
      setCurrentView(viewParam);
      if (window.location.hash.includes('clips')) {
        sessionStorage.setItem('scrollToClips', 'true');
      }
      window.history.replaceState({ section: viewParam }, '', '/' + viewParam);
    }
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Listen for auth modal open events (from PricingSection etc.)
  useEffect(() => {
    const handler = (e) => {
      setAuthMode(e.detail?.mode || 'register');
      setShowAuthModal(true);
    };
    window.addEventListener('open-auth-modal', handler);
    return () => window.removeEventListener('open-auth-modal', handler);
  }, []);

  // Poll for email verification status — auto-dismiss banner once verified
  useEffect(() => {
    if (!user?.isAuthenticated || user?.emailVerified !== false) return;
    const poll = setInterval(async () => {
      try {
        const tk = localStorage.getItem('auth_token');
        if (!tk) return;
        const r = await fetch('/api/auth/me', { headers: { 'Authorization': 'Bearer ' + tk } });
        if (r.ok) {
          const d = await r.json();
          if (d.user && d.user.emailVerified === true) {
            setUser(prev => ({ ...prev, emailVerified: true }));
            clearInterval(poll);
          }
        }
      } catch(e) {}
    }, 10000);
    return () => clearInterval(poll);
  }, [user?.isAuthenticated, user?.emailVerified, setUser]);

  // Custom back function that uses navigation history
  const handleCustomBack = () => {
    if (navigationHistory.length > 1) {
      const newHistory = [...navigationHistory];
      newHistory.pop(); // Remove current page
      const previousPage = newHistory[newHistory.length - 1] || 'dashboard';
      setNavigationHistory(newHistory);
      setCurrentView(previousPage);
      
      // Update browser URL
      if (window.history && window.history.pushState) {
        window.history.pushState({ section: previousPage }, '', `/${previousPage}`);
      }
    } else {
      handleBackToDashboard();
    }
  };

  // If user is authenticated and on /register, redirect to dashboard

  
  // Poll for unread inbox count
  const [, setInboxTick] = React.useState(0);
  React.useEffect(() => {
    if (!user || !user.isAuthenticated || !user.accountId) return;
    const fetchUnread = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const r = await fetch('/api/inbox/' + user.accountId, {
          headers: { 'Authorization': 'Bearer ' + (token || '') }
        });
        if (r.ok) {
          const data = await r.json();
          const msgs = data.messages || [];
          const unread = msgs.filter(m => m.status !== 'read').length;
          if (window.__unreadInboxCount !== unread) {
            window.__unreadInboxCount = unread;
            document.dispatchEvent(new CustomEvent('inbox-update'));
          }
        }
      } catch(e) {}
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [user]);

  React.useEffect(() => {
    const handler = () => setInboxTick(t => t + 1);
    document.addEventListener('inbox-update', handler);
    return () => document.removeEventListener('inbox-update', handler);
  }, []);

  // If user is authenticated, show dashboard or app sections
  if (user.isAuthenticated) {
    if (currentView === 'dashboard') {
      return <><UserDashboard onNavigateToApp={handleNavigateToApp} /><HelpChat /></>;
    }

    // Render specific app sections based on currentView
    return (
      <div className="App">
        {/* <ProcessingIndicator onNavigateToProcessor={handleNavigateToApp} /> */}
        <div className="app-container">
          <header className="header">
            <div className="header-particles" aria-hidden="true">
              <div className="particle p1"></div>
              <div className="particle p2"></div>
              <div className="particle p3"></div>
              <div className="particle p4"></div>
              <div className="particle p5"></div>
              <div className="particle p6"></div>
              <div className="particle p7"></div>
              <div className="particle p8"></div>
              <div className="particle p9"></div>
              <div className="particle p10"></div>
              <div className="particle icon-particle ip1">▶</div>
              <div className="particle icon-particle ip2">✦</div>
              <div className="particle icon-particle ip3">◆</div>
            </div>
            <div className="header-content" style={{position:'relative'}}>
              <div className="logo">
                <div className="logo-icon">🎬</div>
                <h1 className="logo-text">VlogClip AI</h1>
              </div>
              <div className="tagline" style={{display:'flex',alignItems:'center',gap:'10px',justifyContent:'center',flexWrap:'wrap'}}>
                <span>Welcome back, {user.username}!</span>
                <button
                  onClick={() => handleNavigateToApp('notifications')}
                  style={{
                    position:'relative',
                    background:'rgba(255,255,255,0.1)',
                    border:'1px solid rgba(255,255,255,0.2)',
                    borderRadius:'50%',
                    width:'36px',
                    height:'36px',
                    display:'flex',
                    alignItems:'center',
                    justifyContent:'center',
                    cursor:'pointer',
                    fontSize:'18px',
                    transition:'all 0.3s',
                    padding:0
                  }}
                  title="Inbox Notifications"
                >
                  🔔
                  {window.__unreadInboxCount > 0 && (
                    <span style={{
                      position:'absolute',
                      top:'-4px',
                      right:'-4px',
                      background:'#ef4444',
                      color:'white',
                      borderRadius:'50%',
                      width:'18px',
                      height:'18px',
                      fontSize:'11px',
                      fontWeight:'700',
                      display:'flex',
                      alignItems:'center',
                      justifyContent:'center',
                      border:'2px solid #1a1a2e'
                    }}>{window.__unreadInboxCount > 9 ? '9+' : window.__unreadInboxCount}</span>
                  )}
                </button>
                <LanguageSelector />
              </div>
            </div>
            {/* Dashboard button moved to individual page components */}
            <div className="header-gradient"></div>
          </header>

          {user && user.emailVerified === false && (
            <div className="verify-banner">
              <div className="verify-banner-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
                </svg>
              </div>
              <div className="verify-banner-text">
                <span className="verify-banner-title">Email verification required</span>
                <span className="verify-banner-sub">Check your inbox and click the verification link to start processing videos.</span>
              </div>
              <button
                className="verify-banner-btn"
                onClick={async () => {
                  const btn = document.querySelector('.verify-banner-btn');
                  if (btn) { btn.textContent = 'Sending...'; btn.disabled = true; }
                  try {
                    const tk = localStorage.getItem('auth_token');
                    const r = await fetch('/api/auth/resend-verification', { method:'POST', headers:{'Authorization':'Bearer '+tk,'Content-Type':'application/json'} });
                    const d = await r.json();
                    if (btn) { btn.textContent = d.error ? 'Failed' : 'Sent!'; setTimeout(() => { btn.textContent = 'Resend Email'; btn.disabled = false; }, 3000); }
                  } catch(e) { if (btn) { btn.textContent = 'Failed'; setTimeout(() => { btn.textContent = 'Resend Email'; btn.disabled = false; }, 3000); } }
                }}
              >
                Resend Email
              </button>
            </div>
          )}

          {currentView !== 'dashboard' && (
            <div className="breadcrumb-nav">
              <button onClick={handleBackToDashboard} className="breadcrumb-back">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><polyline points="12 19 5 12 12 5"/></svg>
                Dashboard
              </button>
              <span className="breadcrumb-sep">/</span>
              <span className="breadcrumb-current">
                {currentView === 'generator' ? 'Generator' : currentView === 'batch' ? 'Batch Processor' : currentView === 'analytics' ? 'Analytics' : currentView === 'api' ? 'API Access' : currentView === 'notifications' ? 'Notifications' : currentView === 'documentation' ? 'Documentation' : currentView === 'support' ? 'Support' : currentView === 'ai-insights' ? 'AI Insights' : currentView === 'billing' ? 'Billing' : currentView === 'upgrade' || currentView === 'pricing' ? 'Plans & Pricing' : currentView === 'terms' ? 'Terms of Service' : currentView === 'privacy' ? 'Privacy Policy' : currentView.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </span>
            </div>
          )}

          <main className="main-content">
            {currentView === 'generator' && <ClipGenerator />}
            {currentView === 'batch' && <BatchProcessor />}
            {currentView === 'analytics' && <BusinessFeatures />}
            {currentView === 'api' && <ApiAccess />}
            {currentView === 'notifications' && <NotificationsPanel onNavigate={handleNavigateToApp} user={user} />}
            {currentView === 'documentation' && <DocumentationPanel />}
            {currentView === 'support' && <SupportPanel />}
            {/* Removed inbox view - using direct email replies instead */}
            {currentView === 'ai-insights' && <AIInsightsPanel onBack={() => { setCurrentView('dashboard'); if (window.history && window.history.pushState) window.history.pushState({ section: 'dashboard' }, '', '/dashboard'); }} />}
            {currentView === 'billing' && <BillingPanel onNavigate={handleNavigateToApp} user={user} />}
            {(currentView === 'upgrade' || currentView === 'pricing') && <UpgradePanel />}
            {currentView === 'plan-free' && <PlanDetailsPanel plan="free" />}
            {currentView === 'plan-starter' && <PlanDetailsPanel plan="free" />}
            {currentView === 'plan-pro' && <PlanDetailsPanel plan="pro" />}
            {currentView === 'plan-business' && <PlanDetailsPanel plan="business" />}
            {currentView === 'terms' && <TermsOfService />}
            {currentView === 'privacy' && <PrivacyPolicy />}
          </main>
        </div>
        <HelpChat />
      </div>
    );
  }

  // If on referral link and not authenticated, show referral landing page
  const referralCode = new URLSearchParams(window.location.search).get('ref') || sessionStorage.getItem('referralCode');
  if (!user.isAuthenticated && window.location.pathname.startsWith('/register') && referralCode) {
    return (
      <>
        <ReferralLanding 
          referralCode={referralCode}
          onSignUp={(plan) => {
            sessionStorage.setItem('referralCode', referralCode);
            sessionStorage.setItem('referralPlan', plan);
            setShowAuthModal(true);
          }}
        />
        <AuthModal 
          isOpen={showAuthModal} 
          onClose={() => setShowAuthModal(false)}
          initialMode={authMode} 
        />
      </>
    );
  }
  // Redirect unauthenticated users to homepage for any protected path
  const protectedPaths = ['/generator', '/batch', '/dashboard', '/analytics', '/billing', '/notifications', '/support', '/documentation'];
  if (!user.isAuthenticated && protectedPaths.some(p => window.location.pathname.startsWith(p))) {
    window.history.replaceState(null, '', '/');
  }
  // Auto-scroll to pricing section if URL is /pricing
  if (!user.isAuthenticated && window.location.pathname === '/pricing') {
    setTimeout(() => {
      const el = document.getElementById('section-pricing');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 500);
  }

  // If user is not authenticated, show the landing page with login option
  return (
    <>
      <div className="App landing-enhanced">
        <HomepageMobileNav onSignIn={() => setShowAuthModal(true)} />
        <BackToTop />
        <div className="app-container">
          <header className="header">
            <div className="header-content">
              <div className="logo">
                <div className="logo-icon-box">
                  <svg viewBox="0 0 24 24"><path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" fill="white"/></svg>
                </div>
                <h1 className="logo-text">VlogClip AI</h1>
              </div>
              <div className="tagline">
                Turn your content into viral moments
              </div>
            </div>
            
            {/* Auth Buttons Row */}
            <div className="header-auth-row">
              <button onClick={() => { setAuthMode('login'); setShowAuthModal(true); }} className="header-signin-btn">Sign In</button>
              <button onClick={() => { setAuthMode('register'); setShowAuthModal(true); }} className="header-join-btn">Start Free</button>
              <span className="header-desktop-selectors header-left-selectors"><LanguageSelector /></span>
              <div style={{flex:1}} />
              <span className="header-desktop-selectors header-right-selectors"><CurrencySelector /></span>
            </div>
            
            <div className="header-gradient"></div>
          </header>

          <main className="main-content">
            {currentView !== 'terms' && currentView !== 'privacy' && (
            <div className="hero-section">

              {/* Hero Badge */}
              <div className="hero-badge">
                <span className="hero-badge-dot"></span>
                AI-Powered Clip Generation
              </div>

              <h2 className="hero-title">
                Turn one video into a week of <span className="gradient-text">viral content</span>
              </h2>
              <p className="hero-subtitle">
                Paste a YouTube link. AI picks the best moments, adds captions, and delivers clips ready for TikTok, Reels & Shorts.
              </p>

              {/* ===== BIG CTA ===== */}
              <div style={{textAlign:'center',margin:'25px auto 8px'}}>
                <button
                  onClick={() => { setAuthMode('register'); setShowAuthModal(true); }}
                  style={{
                    background:'linear-gradient(135deg,#7c3aed,#6d28d9)',
                    color:'white',fontSize:'18px',fontWeight:'700',
                    padding:'16px 40px',borderRadius:'12px',border:'none',
                    cursor:'pointer',boxShadow:'0 4px 20px rgba(124,58,237,0.4)',
                    transition:'transform 0.2s, box-shadow 0.2s'
                  }}
                  onMouseOver={e => { e.target.style.transform='translateY(-2px)'; e.target.style.boxShadow='0 6px 28px rgba(124,58,237,0.5)'; }}
                  onMouseOut={e => { e.target.style.transform='translateY(0)'; e.target.style.boxShadow='0 4px 20px rgba(124,58,237,0.4)'; }}
                >
                  Start Free — No Credit Card
                </button>
                <p style={{color:'#94a3b8',fontSize:'13px',marginTop:'10px'}}>
                  5 free videos per month. Half the price of Opus Clip.
                </p>
              </div>

              {/* ===== SOCIAL PROOF ===== */}
              <div style={{textAlign:'center',margin:'8px auto 20px',display:'flex',alignItems:'center',justifyContent:'center',gap:'20px',flexWrap:'wrap'}}>
                <span style={{color:'#c4b5fd',fontSize:'13px',fontWeight:'600'}}>60+ creators joined</span>
                <span style={{color:'rgba(148,163,184,0.4)'}}>|</span>
                <span style={{color:'#c4b5fd',fontSize:'13px',fontWeight:'600'}}>11 caption styles</span>
                <span style={{color:'rgba(148,163,184,0.4)'}}>|</span>
                <span style={{color:'#c4b5fd',fontSize:'13px',fontWeight:'600'}}>Clips in under 5 min</span>
              </div>

              {/* ===== VIDEO SHOWCASE (REAL CLIPS) — moved up as primary proof ===== */}
              <VideoShowcase />

              {/* ===== HOW IT WORKS ===== */}
              <div className="how-section" id="section-how">
                <h3>How It Works</h3>
                <div className="steps-row">
                  <div className="step-card">
                    <div className="step-num">1</div>
                    <h4>Paste a URL or Upload</h4>
                    <p>Drop a YouTube link or upload your own video file</p>
                  </div>
                  <div className="step-card">
                    <div className="step-num">2</div>
                    <h4>AI Finds the Best Moments</h4>
                    <p>AI transcribes your video and picks the most viral-worthy moments</p>
                  </div>
                  <div className="step-card">
                    <div className="step-num">3</div>
                    <h4>Download & Post</h4>
                    <p>Get captioned, cropped clips ready for TikTok, Reels & Shorts</p>
                  </div>
                </div>
              </div>

              {/* ===== 5 CLIPS SCREENSHOT — what you get ===== */}
              <div style={{maxWidth:'380px',margin:'0 auto 40px',textAlign:'center'}}>
                <p style={{color:'white',fontSize:'16px',fontWeight:'700',marginBottom:'14px'}}>Your clips, ready to download</p>
                <img
                  src="/images/slider/demo-1.png"
                  alt="5 clips generated from one YouTube video with captions, engagement scores, and thumbnails"
                  width="564"
                  height="964"
                  loading="lazy"
                  decoding="async"
                  style={{width:'100%',height:'auto',aspectRatio:'564/964',borderRadius:'14px',border:'1px solid rgba(255,255,255,0.08)',boxShadow:'0 8px 30px rgba(0,0,0,0.4)',display:'block',background:'#0a0a1a'}}
                />
                <p style={{color:'#94a3b8',fontSize:'13px',marginTop:'12px',lineHeight:'1.5'}}>
                  Each clip comes with engagement scores, AI headlines, share buttons, and one-click download.
                </p>
              </div>

              {/* ===== DEMO VIDEO ===== */}
              <div style={{textAlign:'center',margin:'0 auto 10px'}}>
                <p style={{color:'#94a3b8',fontSize:'14px',fontWeight:'600'}}>See the full process</p>
              </div>
              <div style={{
                maxWidth:'720px',margin:'0 auto 40px',borderRadius:'16px',overflow:'hidden',
                border:'2px solid rgba(124,58,237,0.3)',
                boxShadow:'0 0 40px rgba(124,58,237,0.2), 0 8px 30px rgba(0,0,0,0.4)',
                background:'#000'
              }}>
                <video
                  style={{width:'100%',display:'block'}}
                  controls
                  playsInline
                  preload="metadata"
                  poster="/uploads/demo-poster.jpg"
                >
                  <source src="/uploads/demo-video.mp4" type="video/mp4" />
                  Your browser does not support video playback.
                </video>
              </div>

              {/* ===== KEY FEATURES — unique selling points ===== */}
              {(() => {
                const features = [
                  { title: 'Smart Emoji Detection', desc: 'AI listens to speech, detects keywords, and overlays matching emojis in real-time — 249 categories including Gen Z slang', icon: <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>, highlight: true },
                  { title: '11 Caption Styles', desc: 'Karaoke, Hormozi Bold, Flash, CapCut Pop-In and more — 6 colour options to match your brand', icon: <svg viewBox="0 0 24 24"><path d="M4 7V4h16v3M9 20h6M12 4v16"/></svg> },
                  { title: 'Multi-Video Batch', desc: 'Paste up to 4 YouTube URLs and process them all at once — in parallel, not one at a time', icon: <svg viewBox="0 0 24 24"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8"/><path d="M12 17v4"/><path d="M7 8h2m2 0h2m2 0h2"/></svg>, highlight: true },
                  { title: 'AI Viral Hooks', desc: 'Auto-generated scroll-stopping text on every clip — the hook that grabs attention in the first 3 seconds', icon: <svg viewBox="0 0 24 24"><path d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>, highlight: true },
                  { title: 'Auto Thumbnails', desc: 'Every clip gets a custom thumbnail with bold text overlay — ready to post, no design tools needed', icon: <svg viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg> },
                  { title: 'Upload Your Videos', desc: 'Not just YouTube — upload your own video files (up to 5GB) and get the same AI-powered clips', icon: <svg viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg> },
                ];
                return (
                  <div className="features-strip">
                    {features.map((f, i) => (
                      <div
                        key={i}
                        className="feature-card"
                        style={f.highlight ? {borderColor:'rgba(124,58,237,0.4)',background:'rgba(124,58,237,0.08)',cursor:'pointer'} : {cursor:'pointer'}}
                        onClick={() => setActiveFeature(f)}
                      >
                        <div className="feature-icon-box">{f.icon}</div>
                        <h4>{f.title}</h4>
                        <p>{f.desc}</p>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* ===== FEATURE BOTTOM SHEET (mobile) ===== */}
              {activeFeature && (
                <div
                  style={{position:'fixed',top:0,left:0,right:0,bottom:0,zIndex:9999,display:'flex',alignItems:'flex-end'}}
                  onClick={() => setActiveFeature(null)}
                >
                  <div style={{position:'absolute',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.6)',backdropFilter:'blur(4px)'}} />
                  <div
                    style={{
                      position:'relative',width:'100%',maxWidth:'500px',margin:'0 auto',
                      background:'linear-gradient(180deg,#1e1b4b,#0f0a1e)',
                      borderRadius:'20px 20px 0 0',padding:'28px 24px 40px',
                      border:'1px solid rgba(124,58,237,0.3)',borderBottom:'none',
                      animation:'slideUp 0.3s ease-out'
                    }}
                    onClick={e => e.stopPropagation()}
                  >
                    <div style={{width:'40px',height:'4px',background:'rgba(255,255,255,0.2)',borderRadius:'2px',margin:'0 auto 20px'}} />
                    <button
                      onClick={() => setActiveFeature(null)}
                      style={{position:'absolute',top:'16px',right:'16px',background:'rgba(255,255,255,0.1)',border:'none',color:'white',width:'32px',height:'32px',borderRadius:'50%',fontSize:'18px',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}
                    >x</button>
                    <div style={{textAlign:'center',marginBottom:'16px'}}>
                      <div style={{width:'48px',height:'48px',margin:'0 auto 12px',background:'rgba(124,58,237,0.2)',borderRadius:'12px',display:'flex',alignItems:'center',justifyContent:'center',stroke:'#c4b5fd',strokeWidth:2,fill:'none'}}>
                        {activeFeature.icon}
                      </div>
                      <h3 style={{color:'white',fontSize:'20px',fontWeight:'700',margin:'0 0 12px'}}>{activeFeature.title}</h3>
                    </div>
                    <p style={{color:'#cbd5e1',fontSize:'16px',lineHeight:'1.7',textAlign:'center',margin:0}}>{activeFeature.desc}</p>
                  </div>
                </div>
              )}
              <style>{`@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>

              {/* ===== LANGUAGES BADGE — replaces full slider section ===== */}
              <div style={{textAlign:'center',margin:'40px auto 30px',maxWidth:'600px'}}>
                <div style={{display:'inline-flex',alignItems:'center',gap:'10px',background:'rgba(124,58,237,0.1)',border:'1px solid rgba(124,58,237,0.25)',borderRadius:'50px',padding:'12px 28px'}}>
                  <svg viewBox="0 0 24 24" width="20" height="20" style={{fill:'none',stroke:'#c4b5fd',strokeWidth:2}}>
                    <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                  </svg>
                  <span style={{color:'#c4b5fd',fontSize:'15px',fontWeight:'600'}}>Captions available in 31 languages — from Spanish to Japanese</span>
                </div>
              </div>

              {/* ===== SECOND CTA — warm visitors ready to convert ===== */}
              <div style={{textAlign:'center',margin:'20px auto 40px',padding:'40px 20px',background:'rgba(124,58,237,0.06)',borderRadius:'20px',border:'1px solid rgba(124,58,237,0.15)',maxWidth:'600px'}}>
                <h3 style={{color:'white',fontSize:'22px',fontWeight:'700',margin:'0 0 8px'}}>Ready to create your first clips?</h3>
                <p style={{color:'#94a3b8',fontSize:'14px',margin:'0 0 20px'}}>Free plan. No credit card. Cancel anytime.</p>
                <button
                  onClick={() => { setAuthMode('register'); setShowAuthModal(true); }}
                  style={{
                    background:'linear-gradient(135deg,#7c3aed,#6d28d9)',
                    color:'white',fontSize:'16px',fontWeight:'700',
                    padding:'14px 36px',borderRadius:'12px',border:'none',
                    cursor:'pointer',boxShadow:'0 4px 20px rgba(124,58,237,0.4)',
                    transition:'transform 0.2s, box-shadow 0.2s'
                  }}
                  onMouseOver={e => { e.target.style.transform='translateY(-2px)'; e.target.style.boxShadow='0 6px 28px rgba(124,58,237,0.5)'; }}
                  onMouseOut={e => { e.target.style.transform='translateY(0)'; e.target.style.boxShadow='0 4px 20px rgba(124,58,237,0.4)'; }}
                >
                  Get Started Free
                </button>
              </div>
            </div>

            )}

            {currentView === 'terms' && (
              <div>
                <button onClick={() => setCurrentView('home')} style={{background:'rgba(99,102,241,0.15)',border:'1px solid rgba(99,102,241,0.3)',color:'#a5b4fc',padding:'10px 20px',borderRadius:'10px',cursor:'pointer',fontSize:'14px',fontWeight:'600',marginBottom:'20px'}}>← Return to Home Page</button>
                <TermsOfService />
              </div>
            )}
            {currentView === 'privacy' && (
              <div>
                <button onClick={() => setCurrentView('home')} style={{background:'rgba(99,102,241,0.15)',border:'1px solid rgba(99,102,241,0.3)',color:'#a5b4fc',padding:'10px 20px',borderRadius:'10px',cursor:'pointer',fontSize:'14px',fontWeight:'600',marginBottom:'20px'}}>← Return to Home Page</button>
                <PrivacyPolicy />
              </div>
            )}
          </main>

          {/* Pricing Section */}
          {currentView !== 'terms' && currentView !== 'privacy' && <div id="section-pricing"><PricingSection /></div>}
          {currentView !== 'terms' && currentView !== 'privacy' && (

          <div className="cta-section-enhanced">
            <h3>Try it free</h3>
            <p>No credit card required. Generate your first clips in minutes.</p>
            <button 
              className="cta-btn-enhanced"
              onClick={() => { setAuthMode('register'); setShowAuthModal(true); }}
            >
              <svg viewBox="0 0 24 24"><path d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
              Get Started Free
            </button>
            <p className="cta-sub-text">No credit card required · Free tier available</p>
          </div>
          )}

          {/* ===== FOOTER ===== */}
          <footer className="footer-enhanced">
            <div style={{maxWidth:'480px',margin:'0 auto 24px',textAlign:'center',padding:'20px',background:'rgba(255,255,255,0.03)',borderRadius:'12px',border:'1px solid rgba(255,255,255,0.06)'}}>
              <div style={{color:'#e2e8f0',fontSize:'14px',fontWeight:'700',marginBottom:'6px'}}>Built by Dwight F.H</div>
              <div style={{color:'#94a3b8',fontSize:'13px',lineHeight:'1.6'}}>
                I built VlogClip AI on my own because I saw creators paying too much for clip tools that nickel-and-dime you with credit systems. I've tried to be as fair with the pricing as possible — and I hope anyone who joins will be able to get the best out of their content creation.
              </div>
            </div>
            <p>© 2026 VlogClip AI</p>
            <div className="footer-links-enhanced">
              <a href="/terms" onClick={(e) => { e.preventDefault(); handleNavigateToApp('terms'); }}>Terms of Service</a>
              <a href="/privacy" onClick={(e) => { e.preventDefault(); handleNavigateToApp('privacy'); }}>Privacy Policy</a>
            </div>
          </footer>
        </div>
      </div>
      
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)}
        initialMode={authMode} 
      />
    </>
  );
};

function App() {
  return (
    <CurrencyProvider>
      <ProcessingProvider>
        <UserProvider>
          <AppContent />
        </UserProvider>
      </ProcessingProvider>
    </CurrencyProvider>
  );
}

export default App;
